import React, { CSSProperties, useCallback, useEffect, useState } from "react";

import {
  Button,
  Divider,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Segment,
  Statistic,
  Table,
} from "semantic-ui-react";

import {
  AppointmentCalendarAPI,
  CalendarHealthAPI,
  DivisionCalendarAPI,
  type CalendarEventRow,
  type CalendarHealth,
  type DivisionCalendarInfo,
} from "react-lib/apis/issara/apps/QUE/CalendarSync";

type SettingGoogleCalendarProps = {
  apiToken: string;
  division: number;
  divisionName?: string;
};

const styles = {
  row: { display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" } as CSSProperties,
  label: { minWidth: "150px", fontWeight: 600 } as CSSProperties,
  mono: { fontFamily: "monospace", fontSize: "11px", wordBreak: "break-all" as const } as CSSProperties,
  hint: { color: "#777", fontSize: "11px" } as CSSProperties,
  listRow: { display: "flex", gap: "0.5rem", alignItems: "center" } as CSSProperties,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SettingGoogleCalendar: React.FC<SettingGoogleCalendarProps> = (props) => {
  const [health, setHealth] = useState<CalendarHealth | null>(null);
  const [binding, setBinding] = useState<DivisionCalendarInfo | null>(null);
  const [events, setEvents] = useState<CalendarEventRow[]>([]);
  const [calendarIdInput, setCalendarIdInput] = useState("");
  const [staffEmails, setStaffEmails] = useState<string[]>([]);
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailTarget, setEmailTarget] = useState<"staff" | "share">("staff");
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const divisionId = props.division;

  const loadHealth = useCallback(async () => {
    try {
      const data = await CalendarHealthAPI.get({ apiToken: props.apiToken });
      setHealth(data);
    } catch (e: any) {
      setNotice({ type: "error", text: `อ่านสถานะระบบไม่สำเร็จ: ${e?.message || e}` });
    }
  }, [props.apiToken]);

  const loadBinding = useCallback(async () => {
    if (!divisionId) return;
    try {
      const info = await DivisionCalendarAPI.get({ divisionId, apiToken: props.apiToken });
      if (info.calendar_id) {
        setBinding(info);
        setStaffEmails(info.staff_emails ?? []);
        setShareEmails(info.share_emails ?? []);
        setSyncEnabled(info.sync_enabled ?? true);
        setIsPublic(info.is_public ?? false);
      } else {
        setBinding(null);
      }
    } catch (e: any) {
      setNotice({ type: "error", text: `อ่านปฏิทินของแผนกไม่สำเร็จ: ${e?.message || e}` });
    }
  }, [divisionId, props.apiToken]);

  const loadEvents = useCallback(async () => {
    if (!binding?.calendar_id) return;
    try {
      const { results } = await AppointmentCalendarAPI.eventsByCalendar({
        calendarId: binding.calendar_id,
        apiToken: props.apiToken,
      });
      setEvents(results);
    } catch {
      setEvents([]);
    }
  }, [binding?.calendar_id, props.apiToken]);

  useEffect(() => {
    loadHealth();
    loadBinding();
  }, [loadHealth, loadBinding]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const save = async (payload: Record<string, any>, successText: string) => {
    if (!divisionId) return;
    setBusy(true);
    setNotice(null);
    try {
      const info = await DivisionCalendarAPI.provision({
        divisionId,
        apiToken: props.apiToken,
        staffEmails,
        shareEmails,
        syncEnabled,
        isPublic,
        ...payload,
      });
      setBinding(info);
      setNotice({ type: "success", text: successText });
      loadHealth();
    } catch (e: any) {
      setNotice({
        type: "error",
        text: e?.response?.data?.detail || e?.message || "บันทึกไม่สำเร็จ",
      });
    } finally {
      setBusy(false);
    }
  };

  const addEmail = () => {
    const value = emailDraft.trim().toLowerCase();
    if (!value) return;
    if (!emailPattern.test(value)) {
      setNotice({ type: "error", text: `อีเมลไม่ถูกต้อง: ${value}` });
      return;
    }
    const target = emailTarget === "staff" ? staffEmails : shareEmails;
    const next = Array.from(new Set([...target, value]));
    if (emailTarget === "staff") setStaffEmails(next);
    else setShareEmails(next);
    setEmailDraft("");
    setNotice(null);
  };

  const removeEmail = (kind: "staff" | "share", email: string) => {
    if (kind === "staff") setStaffEmails(staffEmails.filter((e) => e !== email));
    else setShareEmails(shareEmails.filter((e) => e !== email));
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    setNotice({ type: "success", text: "คัดลอกลิงก์แล้ว" });
  };

  const delegated = health?.domain_wide_delegation;
  const bound = Boolean(binding?.calendar_id);

  return (
    <div style={{ padding: "1rem 0.5rem", width: "100%" }}>
      <Header as="h4" style={{ color: "#5dbcd2" }}>
        <Icon name="calendar alternate outline" />
        <Header.Content>
          Google Calendar ของแผนก
          <Header.Subheader style={styles.hint}>
            นัดหมายที่สร้างในแผนกนี้จะซิงก์ขึ้นปฏิทินกลางของ MeHealth และส่งคำเชิญไปหาผู้รับบริการ
          </Header.Subheader>
        </Header.Content>
      </Header>

      {notice && (
        <Message
          positive={notice.type === "success"}
          negative={notice.type === "error"}
          size="small"
          content={notice.text}
          onDismiss={() => setNotice(null)}
        />
      )}

      {/* สถานะระบบ */}
      <Segment style={{ background: "#fafafa" }}>
        <Header as="h5">สถานะระบบ</Header>
        <Table basic="very" size="small" compact>
          <Table.Body>
            <Table.Row>
              <Table.Cell style={styles.label}>Service account</Table.Cell>
              <Table.Cell>
                {health?.configured ? (
                  <Label color="green" size="tiny">
                    <Icon name="check" /> พร้อมใช้
                  </Label>
                ) : (
                  <Label color="red" size="tiny">
                    <Icon name="close" /> ยังไม่ได้ตั้งค่า
                  </Label>
                )}
                <div style={styles.mono}>{health?.service_account_email || "-"}</div>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell style={styles.label}>Domain-Wide Delegation</Table.Cell>
              <Table.Cell>
                {delegated ? (
                  <Label color="green" size="tiny">
                    <Icon name="check" /> impersonate ได้
                  </Label>
                ) : (
                  <Label color="yellow" size="tiny">
                    <Icon name="warning" /> ยัง impersonate ไม่ได้
                  </Label>
                )}
                <div style={styles.mono}>
                  user: {health?.impersonate_user || "(ว่าง)"} ·{" "}
                  {health?.credential_source || "ไม่พบ credential"}
                </div>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell style={styles.label}>วิธีส่งคำเชิญ</Table.Cell>
              <Table.Cell>
                {health?.invite_mode === "google_sendUpdates"
                  ? "Google เป็นผู้ส่งอีเมลคำเชิญให้ผู้ป่วย (sendUpdates=all)"
                  : "ระบบส่งอีเมลแนบไฟล์ .ics ให้เอง (ใช้เมื่อ impersonate ไม่ได้)"}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        <div style={styles.row}>
          <Statistic size="mini">
            <Statistic.Value>{health?.bound_divisions ?? 0}</Statistic.Value>
            <Statistic.Label>แผนกที่ผูกปฏิทิน</Statistic.Label>
          </Statistic>
          <Statistic size="mini">
            <Statistic.Value>{health?.synced_events ?? 0}</Statistic.Value>
            <Statistic.Label>event ที่ซิงก์แล้ว</Statistic.Label>
          </Statistic>
          <Button size="mini" basic icon="refresh" onClick={loadHealth} loading={busy} />
        </div>
        {!delegated && (
          <Message
            size="tiny"
            warning
            header="ยัง impersonate ไม่ได้"
            list={[
              "ตั้ง GOOGLE_CALENDAR_IMPERSONATE_USER เป็นอีเมลผู้ใช้ใน Google Workspace ที่ grant ไว้",
              "grant client id ของ service account ใน Admin console (Domain-wide delegation) ให้ครอบโดเมนของ user นั้น",
              "user ที่ impersonate ต้องมีอยู่จริงในโดเมนนั้น (email ที่ไม่มีบัญชี จะ unauthorized_client)",
            ]}
          />
        )}
      </Segment>

      <Divider />

      {/* ปฏิทินของแผนก */}
      <Segment style={{ background: "#fafafa" }}>
        <Header as="h5">ปฏิทินของแผนก {props.divisionName ? `· ${props.divisionName}` : ""}</Header>
        {bound ? (
          <div>
            <div style={styles.listRow}>
              <Icon name="check circle" color="green" />
              <span style={styles.mono}>{binding?.calendar_id}</span>
            </div>
            <div style={{ ...styles.hint, marginLeft: "1.6rem" }}>
              {binding?.summary} · {binding?.time_zone} · {binding?.events ?? 0} event
            </div>
            <Divider fitted />
            <div style={{ ...styles.row, marginTop: "0.6rem" }}>
              <Button
                size="small"
                primary
                icon="save"
                content="บันทึกการตั้งค่าปฏิทิน"
                loading={busy}
                onClick={() => save({}, "บันทึกการตั้งค่าแล้ว")}
              />
            </div>
          </div>
        ) : (
          <Message size="tiny" info>
            <div>
              แผนกนี้ยังไม่มีปฏิทินของ MeHealth — สร้างใหม่จากที่นี่ หรือระบุ calendar id ที่แผนกสร้างเองใน
              Google Calendar แล้วให้ระบบขอสิทธิ์เขียน
            </div>
          </Message>
        )}

        <div style={{ ...styles.row, marginTop: "0.8rem" }}>
          <Input
            size="small"
            action={{
              icon: "google",
              onClick: () => save({ calendarId: calendarIdInput || undefined }, calendarIdInput ? "ผูกปฏิทินที่มีอยู่แล้ว" : "สร้างปฏิทินใหม่ให้แผนกนี้แล้ว"),
            }}
            placeholder={bound ? "เปลี่ยน calendar อื่น (เว้นว่าง = สร้างใหม่)" : "เว้นว่างเพื่อสร้างใหม่ หรือใส่ calendar id"}
            value={calendarIdInput}
            onChange={(e) => setCalendarIdInput(e.target.value)}
            disabled={busy}
            style={{ minWidth: "340px" }}
          />
        </div>
      </Segment>

      <Divider />

      {/* ตั้งค่าการซิงก์ */}
      <Segment style={{ background: "#fafafa" }}>
        <Header as="h5">การซิงก์</Header>
        <div style={styles.listRow}>
          <Button
            size="mini"
            basic
            color={syncEnabled ? "green" : "grey"}
            content={syncEnabled ? "Auto sync เปิด" : "Auto sync ปิด"}
            onClick={() => setSyncEnabled(!syncEnabled)}
          />
          <span style={styles.hint}>เปิด = ทุกนัดที่สร้างในแผนกนี้จะถูกซิงก์ขึ้นปฏิทินอัตโนมัติ</span>
        </div>

        <Divider />

        <Header as="h6">
          <Icon name="users" /> เจ้าหน้าที่ (writer · ได้รับคำเชิญทุกนัด)
        </Header>
        <div style={styles.row}>
          {staffEmails.map((email) => (
            <Label key={email} size="tiny" onRemove={() => removeEmail("staff", email)}>
              {email}
            </Label>
          ))}
          {staffEmails.length === 0 && <span style={styles.hint}>ยังไม่มี</span>}
        </div>

        <Header as="h6">
          <Icon name="eye" /> ผู้ที่ดูปฏิทินได้ (reader)
        </Header>
        <div style={styles.row}>
          {shareEmails.map((email) => (
            <Label key={email} size="tiny" color="teal" onRemove={() => removeEmail("share", email)}>
              {email}
            </Label>
          ))}
          {shareEmails.length === 0 && <span style={styles.hint}>ยังไม่มี</span>}
        </div>

        <div style={{ ...styles.row, marginTop: "0.6rem" }}>
          <Button
            size="mini"
            basic
            color={emailTarget === "staff" ? "pink" : "teal"}
            content={emailTarget === "staff" ? "เพิ่มเป็น เจ้าหน้าที่" : "เพิ่มเป็น ผู้ดู"}
            onClick={() => setEmailTarget(emailTarget === "staff" ? "share" : "staff")}
          />
          <Input
            size="small"
            placeholder="name@morishealth.com"
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addEmail()}
            action={{ icon: "plus", onClick: addEmail }}
            style={{ minWidth: "260px" }}
          />
        </div>

        <div style={{ ...styles.row, marginTop: "0.8rem" }}>
          <Button
            size="small"
            primary
            icon="save"
            content="บันทึกการซิงก์ + รายชื่อ"
            loading={busy}
            onClick={() => save({}, "บันทึกแล้ว")}
          />
        </div>
      </Segment>

      <Divider />

      {/* ลิงก์ส่งต่อ */}
      {bound && binding?.links && (
        <Segment style={{ background: "#fafafa" }}>
          <Header as="h5">
            <Icon name="share alternate" /> ส่งต่อให้คนอื่นดู
          </Header>
          <Message size="tiny" info>
            ส่งลิงก์นี้ให้เจ้าหน้าที่ที่ถูกเพิ่มเป็น reader แล้ว กด “add to Google Calendar” ครั้งเดียว
            ปฏิทินของแผนกจะโผล่ใน list “Other calendars” ของเขา
          </Message>
          <div style={styles.listRow}>
            <a href={binding.links.subscribe_link} target="_blank" rel="noreferrer">
              ลิงก์เพิ่มปฏิทิน (subscribe)
            </a>
            <Button size="mini" basic icon="copy" content="copy" onClick={() => copy(binding.links!.subscribe_link)} />
          </div>
          <div style={{ ...styles.mono, marginTop: "0.4rem", color: "#888" }}>
            {binding.links.subscribe_link}
          </div>

          <Divider />
          <div style={styles.listRow}>
            <Button
              size="mini"
              basic
              color={isPublic ? "orange" : "grey"}
              content={isPublic ? "เปิดให้คนนอกเข้าถึงผ่านลิงก์" : "จำกัดเฉพาะคนที่มีสิทธิ์"}
              onClick={() => setIsPublic(!isPublic)}
            />
            <span style={styles.hint}>
              ปิดไว้ = มีเฉพาะคนที่ add เข้าปฏิทินเท่านั้นที่เห็น (ค่าเริ่มต้น)
            </span>
          </div>
          {isPublic && (
            <div style={{ ...styles.row, marginTop: "0.5rem" }}>
              <a href={binding.links.embed_url} target="_blank" rel="noreferrer">
                embedded view
              </a>
              <a href={binding.links.ics_url} target="_blank" rel="noreferrer">
                ics feed
              </a>
            </div>
          )}
        </Segment>
      )}

      {/* event ล่าสุด */}
      {bound && events.length > 0 && (
        <>
          <Divider />
          <Segment style={{ background: "#fafafa" }}>
            <Header as="h5">event ล่าสุดบนปฏิทินแผนก</Header>
            <Table celled size="small" compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>นัด</Table.HeaderCell>
                  <Table.HeaderCell>ฝ่าย</Table.HeaderCell>
                  <Table.HeaderCell>สถานะ</Table.HeaderCell>
                  <Table.HeaderCell>ลิงก์</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {events.slice(0, 10).map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell>{row.patient_appointment}</Table.Cell>
                    <Table.Cell>{row.side}</Table.Cell>
                    <Table.Cell>
                      {row.synced ? (
                        <Label color="green" size="mini">
                          synced
                        </Label>
                      ) : (
                        <Label color="red" size="mini">
                          fail
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {row.html_link ? (
                        <a href={row.html_link} target="_blank" rel="noreferrer">
                          เปิด event
                        </a>
                      ) : (
                        row.error || "-"
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </>
      )}
    </div>
  );
};

export default React.memo(SettingGoogleCalendar);
